// components/FeaturesShowcase.jsx
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const FeaturesShowcase = () => {
  const [activeFeature, setActiveFeature] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  const features = [
    {
      id: 0,
      title: "Live Expert-Led Training",
      description: "Interactive compliance webinars with real-time Q&A, case studies, and regulatory updates",
      image: "https://images.unsplash.com/photo-1552664730-d307ca884978?w=600&h=400&fit=crop",
      benefits: ["Real-time interaction", "Expert guidance", "CPE credits"]
    },
    {
      id: 1,
      title: "On-Demand Learning Library",
      description: "Access 500+ recorded compliance courses covering OSHA, HR, HIPAA, and industry regulations",
      image: "https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=600&h=400&fit=crop",
      benefits: ["24/7 access", "Self-paced learning", "Certificate downloads"]
    },
    {
      id: 2,
      title: "Certification & Credits",
      description: "Earn SHRM, HRCI, and CPE credits with comprehensive compliance certification programs",
      image: "https://images.unsplash.com/photo-1434030216411-0b793f4b4173?w=600&h=400&fit=crop",
      benefits: ["Industry recognized", "Career advancement", "Compliance proof"]
    }
  ];

  // Auto-rotation effect
  useEffect(() => {
    if (isPaused) return;

    const interval = setInterval(() => {
      setActiveFeature((prevFeature) => (prevFeature + 1) % features.length);
    }, 5000); // Change every 5 seconds

    return () => clearInterval(interval);
  }, [features.length, isPaused]);

  // Pause auto-rotation on hover
  const handleMouseEnter = () => {
    setIsPaused(true);
  };

  const handleMouseLeave = () => {
    setIsPaused(false);
  };

  // Manual feature selection
  const handleFeatureClick = (index) => {
    setActiveFeature(index);
    setIsPaused(true);
    
    // Resume auto-rotation after 3 seconds
    setTimeout(() => {
      setIsPaused(false);
    }, 3000);
  };

  return (
    <section className="py-16 lg:py-20 bg-gradient-to-br from-green-50 via-emerald-50/50 to-green-50 relative overflow-hidden">
      {/* Decorative background elements */}
      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-green-500 via-emerald-500 to-green-500"></div>
      <div className="absolute -top-20 -right-20 w-80 h-80 bg-green-200/20 rounded-full blur-3xl"></div>
      <div className="absolute -bottom-20 -left-20 w-80 h-80 bg-emerald-200/20 rounded-full blur-3xl"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12 lg:mb-16"
        >
          <motion.div 
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-green-100 to-emerald-100 rounded-full border border-green-200 text-sm mb-6"
          >
            <motion.div
              animate={{ 
                scale: [1, 1.2, 1],
                boxShadow: [
                  "0 0 0 0 rgba(16, 185, 129, 0)",
                  "0 0 0 6px rgba(16, 185, 129, 0.3)",
                  "0 0 0 0 rgba(16, 185, 129, 0)"
                ]
              }}
              transition={{ 
                duration: 2,
                repeat: Infinity,
                ease: "easeInOut"
              }}
              className="w-2 h-2 bg-gradient-to-r from-green-500 to-emerald-600 rounded-full"
            ></motion.div>
            <span className="font-semibold text-green-800">Platform Features</span>
          </motion.div>

          <motion.h2 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            viewport={{ once: true }}
            className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4"
          >
            <motion.span 
              className="bg-gradient-to-r from-emerald-700 via-green-600 to-emerald-700 bg-clip-text text-transparent"
              animate={{ 
                backgroundPosition: ['0% 50%', '100% 50%', '0% 50%'],
              }}
              transition={{ 
                duration: 5, 
                repeat: Infinity,
                ease: "linear" 
              }}
              style={{ backgroundSize: '200% 100%' }}
            >
              Your Complete Compliance Solution
            </motion.span>
          </motion.h2>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            viewport={{ once: true }}
            className="text-base sm:text-lg lg:text-xl text-gray-600 max-w-2xl mx-auto"
          >
            Everything you need to stay compliant, certified, and ahead of regulatory changes
          </motion.p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          {/* Feature Tabs */}
          <div 
            className="space-y-4"
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            {features.map((feature, index) => (
              <motion.div
                key={feature.id}
                className={`p-6 rounded-2xl cursor-pointer transition-all duration-300 relative overflow-hidden ${
                  activeFeature === index 
                    ? 'bg-gradient-to-br from-white to-emerald-50 shadow-2xl border-2 border-green-400' 
                    : 'bg-white/90 hover:bg-white hover:shadow-xl border-2 border-transparent'
                }`}
                onClick={() => handleFeatureClick(index)}
                whileHover={{ x: 8, scale: 1.01 }}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1, type: "spring", stiffness: 100 }}
              >
                {/* Active feature indicator */}
                {activeFeature === index && (
                  <motion.div 
                    className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-green-500 via-emerald-500 to-green-500"
                    layoutId="activeFeatureIndicator"
                  />
                )}

                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <motion.div 
                      className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all duration-300 shadow-lg ${
                        activeFeature === index
                          ? 'bg-gradient-to-br from-green-500 to-emerald-600'
                          : 'bg-gradient-to-br from-green-100 to-emerald-100'
                      }`}
                      whileHover={{ rotate: 5 }}
                    >
                      <span className={`text-lg font-bold ${
                        activeFeature === index ? 'text-white' : 'text-green-700'
                      }`}>
                        {index + 1}
                      </span>
                    </motion.div>
                    <h3 className={`text-xl font-bold transition-colors duration-300 ${
                      activeFeature === index ? 'text-green-700' : 'text-gray-900'
                    }`}>
                      {feature.title}
                    </h3>
                  </div>
                  
                  {/* Progress indicator for active feature */}
                  {activeFeature === index && !isPaused && (
                    <div className="w-12 h-1 bg-green-100 rounded-full overflow-hidden">
                      <motion.div
                        className="h-full bg-gradient-to-r from-green-500 to-emerald-500 rounded-full"
                        initial={{ width: "0%" }}
                        animate={{ width: "100%" }}
                        transition={{ duration: 5, ease: "linear" }}
                        key={`progress-${activeFeature}`}
                      />
                    </div>
                  )}
                </div>
                
                <p className={`mb-4 transition-colors duration-300 ${
                  activeFeature === index ? 'text-gray-700' : 'text-gray-600'
                }`}>
                  {feature.description}
                </p>
                
                <div className="flex flex-wrap gap-2">
                  {feature.benefits.map((benefit, i) => (
                    <motion.span 
                      key={i}
                      initial={{ scale: 0.8, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      transition={{ delay: i * 0.1 }}
                      whileHover={{ scale: 1.05 }}
                      className={`px-3 py-1 text-xs font-semibold rounded-full transition-all duration-300 ${
                        activeFeature === index
                          ? 'bg-gradient-to-r from-green-100 to-emerald-100 text-green-800 border border-green-200 shadow-sm'
                          : 'bg-gradient-to-r from-green-50 to-emerald-50 text-gray-700 border border-green-100'
                      }`}
                    >
                      {benefit}
                    </motion.span>
                  ))}
                </div>
              </motion.div>
            ))}
            
            {/* Navigation dots */}
            <motion.div 
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="flex justify-center space-x-3 pt-6"
            >
              {features.map((_, index) => (
                <motion.button
                  key={index}
                  onClick={() => handleFeatureClick(index)}
                  whileHover={{ scale: 1.2 }}
                  whileTap={{ scale: 0.8 }}
                  className={`rounded-full transition-all duration-300 ${
                    activeFeature === index
                      ? 'w-8 h-3 bg-gradient-to-r from-green-500 to-emerald-500 shadow-lg shadow-green-500/30'
                      : 'w-3 h-3 bg-green-200 hover:bg-green-300'
                  }`}
                  aria-label={`Go to feature ${index + 1}`}
                />
              ))}
            </motion.div>
          </div>

          {/* Feature Image */}
          <div className="relative">
            <div className="relative rounded-3xl overflow-hidden shadow-2xl border-2 border-green-100 group">
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeFeature}
                  initial={{ opacity: 0, scale: 1.1 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={{ duration: 0.6, type: "spring" }}
                  className="relative"
                >
                  <img
                    src={features[activeFeature].image}
                    alt={features[activeFeature].title}
                    className="w-full h-96 lg:h-[500px] object-cover"
                    loading="lazy"
                  />
                  
                  {/* Gradient overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent pointer-events-none" />
                  
                  {/* Feature title overlay */}
                  <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/70 to-transparent">
                    <motion.h4
                      key={`title-${activeFeature}`}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.3 }}
                      className="text-white text-2xl font-bold mb-2"
                    >
                      {features[activeFeature].title}
                    </motion.h4>
                    <motion.p
                      key={`desc-${activeFeature}`}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.4 }}
                      className="text-white/90 text-sm"
                    >
                      {features[activeFeature].description}
                    </motion.p>
                  </div>
                </motion.div>
              </AnimatePresence>
              
              {/* Hover effect overlay */}
              <div className="absolute inset-0 bg-gradient-to-r from-green-500/0 via-emerald-500/0 to-green-500/0 group-hover:from-green-500/10 group-hover:via-emerald-500/10 group-hover:to-green-500/10 transition-all duration-500 pointer-events-none" />
            </div>
            
            {/* Play/Pause indicator */}
            <motion.div 
              className="absolute top-4 right-4 bg-white/95 backdrop-blur-sm rounded-xl p-3 shadow-xl border border-green-200"
              whileHover={{ scale: 1.1, rotate: 5 }}
              whileTap={{ scale: 0.95 }}
            >
              <div className="flex items-center gap-2">
                <motion.div 
                  className={`w-3 h-3 rounded-full ${
                    isPaused ? 'bg-yellow-400' : 'bg-green-400'
                  }`}
                  animate={isPaused ? {} : { scale: [1, 1.2, 1] }}
                  transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
                />
                <span className="text-xs font-semibold text-gray-700">
                  {isPaused ? 'Paused' : 'Auto'}
                </span>
              </div>
            </motion.div>

            {/* Feature counter badge */}
            <motion.div 
              className="absolute top-4 left-4 bg-gradient-to-r from-green-500 to-emerald-600 rounded-full px-4 py-2 shadow-xl"
              key={activeFeature}
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ type: "spring", stiffness: 200 }}
            >
              <span className="text-white font-bold text-sm flex items-center gap-1">
                <span>{activeFeature + 1}</span>
                <span className="text-white/70">/</span>
                <span>{features.length}</span>
              </span>
            </motion.div>

            {/* Floating benefit badges */}
            <motion.div 
              className="absolute -bottom-4 -right-4 bg-white rounded-xl p-4 shadow-2xl border border-green-100 max-w-[200px]"
              initial={{ opacity: 0, x: 20, y: 20 }}
              animate={{ opacity: 1, x: 0, y: 0 }}
              transition={{ delay: 0.5, type: "spring" }}
              whileHover={{ scale: 1.05, rotate: -2 }}
            >
              <div className="text-sm font-bold text-green-700 mb-2">Key Benefits</div>
              <div className="space-y-1">
                {features[activeFeature].benefits.map((benefit, i) => (
                  <motion.div 
                    key={i}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.6 + i * 0.1 }}
                    className="flex items-center gap-2 text-xs text-gray-600"
                  >
                    <div className="w-1.5 h-1.5 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full"></div>
                    <span>{benefit}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>

        {/* Stats counter */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.6 }}
          className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12 lg:mt-16 pt-8 border-t border-green-100"
        >
          {[
            { value: '500+', label: 'Compliance Courses', icon: '📚' },
            { value: '50K+', label: 'Professionals Trained', icon: '👥' },
            { value: '98%', label: 'Satisfaction Rate', icon: '⭐' }
          ].map((stat, index) => (
            <motion.div 
              key={index}
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.7 + index * 0.1 }}
              whileHover={{ y: -5 }}
              className="text-center p-6 bg-gradient-to-br from-white to-emerald-50 rounded-2xl border border-green-100 shadow-sm hover:shadow-md transition-all"
            >
              <div className="text-4xl mb-2">{stat.icon}</div>
              <motion.div 
                className="text-3xl font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent mb-2"
                animate={{ 
                  textShadow: [
                    '0 0 0px rgba(16, 185, 129, 0)',
                    '0 0 10px rgba(16, 185, 129, 0.3)',
                    '0 0 0px rgba(16, 185, 129, 0)'
                  ]
                }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                {stat.value}
              </motion.div>
              <div className="text-gray-600 font-medium">{stat.label}</div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default FeaturesShowcase;